import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AuthApi, setAuthSession } from "@/lib/apiClient";
import { Radar, Lock, User, Loader2 } from "lucide-react";

const ACCENT = "#6E93C7";
const BG = "#3A3A3C";
const BG_DARK = "#2E2E30";
const BORDER = "rgba(255,255,255,0.12)";

function formatErr(detail) {
  if (detail == null) return "Errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) { setError("Inserisci nome utente e password"); return; }
    setLoading(true);
    try {
      const { token, user } = await AuthApi.login(username.trim(), password);
      setAuthSession(token, user);
      onSuccess && onSuccess(user);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 relative overflow-hidden"
      style={{ background: BG, fontFamily: "'Avenir Next', 'Segoe UI', Helvetica, Arial, sans-serif" }}>
      {/* Skyline di Milano (Porta Nuova) al tramonto — tonalità violacee */}
      <div className="absolute inset-0" aria-hidden="true" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1645115130719-968d40a51ee1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')",
        backgroundSize: "cover", backgroundPosition: "center",
      }} />
      <div className="absolute inset-0" aria-hidden="true" style={{
        background: "linear-gradient(180deg, rgba(30,26,46,0.45) 0%, rgba(30,26,46,0.62) 55%, rgba(24,22,40,0.80) 100%)",
      }} />
      <div className="w-full max-w-sm relative z-10">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <Radar className="h-7 w-7" style={{ color: ACCENT }} />
          <div className="text-white">
            <div className="font-black tracking-tight text-2xl leading-none">LeadRadar</div>
            <div className="text-[11px] tracking-widest uppercase" style={{ color: "rgba(255,255,255,0.55)" }}>SIACASA · Riservato</div>
          </div>
        </div>
        <form onSubmit={submit} data-testid="login-form"
          className="border p-7 space-y-4 backdrop-blur-md" style={{ borderColor: BORDER, background: "rgba(46,46,48,0.78)" }}>
          <div>
            <h1 className="text-white font-bold text-lg">Accedi</h1>
            <p className="text-white/50 text-sm mt-0.5">Entra con il tuo nome utente e password.</p>
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-white/50">Nome utente</label>
            <div className="relative">
              <User className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input data-testid="login-username" value={username} onChange={(e) => setUsername(e.target.value)}
                placeholder="Stefano" autoFocus
                className="pl-9 border-white/15 text-white rounded-none focus-visible:ring-[#6E93C7]" style={{ background: BG }} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-white/50">Password</label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-9 border-white/15 text-white rounded-none focus-visible:ring-[#6E93C7]" style={{ background: BG }} />
            </div>
          </div>
          {error && <div data-testid="login-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 px-3 py-2">{error}</div>}
          <Button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full h-11 rounded-none text-white font-semibold hover:brightness-110" style={{ background: ACCENT }}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
          </Button>
        </form>
      </div>
    </div>
  );
}
